import React, {
  useState,
  useCallback,
  useContext,
  useMemo,
  useEffect,
} from "react";
import { Modal } from "react-bootstrap";
import { Context } from "../App";
import {
  isSlotDisabled,
} from "../utils/slotAvailability";
import { toDateInputValue } from "../utils/eventDate";
import {
  resolveTheaterGallery,
  getTheaterMediaEntry,
  absoluteMediaUrl,
  normalizeTheaterFolderKey,
} from "../utils/theaterGalleryImages";
import {
  findApplicableSlotOffer,
  mergeSlotWithOffer,
} from "../utils/slotTimeOffers";
import { useCms } from "../context/CmsContext";
import "../partyBossTheme.css";

function youtubeEmbedFromUrl(url) {
  if (!url || typeof url !== "string") return null;
  try {
    const u = new URL(url.startsWith("http") ? url : `https://${url}`);
    if (u.hostname.includes("youtu.be")) {
      const id = u.pathname.replace(/^\//, "").split("/")[0];
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (u.hostname.includes("youtube.com") || u.hostname.includes("youtube-nocookie.com")) {
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
      const m = u.pathname.match(/\/embed\/([^/]+)/);
      if (m) return `https://www.youtube.com/embed/${m[1]}`;
    }
  } catch {
    return null;
  }
  return null;
}

function vimeoEmbedFromUrl(url) {
  if (!url || typeof url !== "string") return null;
  try {
    const u = new URL(url.startsWith("http") ? url : `https://${url}`);
    if (!u.hostname.includes("vimeo.com")) return null;
    const m = u.pathname.match(/\/(?:video\/)?(\d+)/);
    return m ? `https://player.vimeo.com/video/${m[1]}` : null;
  } catch {
    return null;
  }
}

function resolvePromoVideoEmbed(raw) {
  if (raw == null) return { kind: "video", src: "" };
  const trimmed = String(raw).trim();
  if (!trimmed) return { kind: "video", src: "" };
  const yt = youtubeEmbedFromUrl(trimmed);
  if (yt) return { kind: "iframe", src: yt };
  const vm = vimeoEmbedFromUrl(trimmed);
  if (vm) return { kind: "iframe", src: vm };
  const abs = absoluteMediaUrl(trimmed);
  if (/\.(mp4|webm|mov)(\?|#|$)/i.test(abs) || /\/theatre-videos\//i.test(abs)) {
    return { kind: "video", src: abs };
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return { kind: "iframe", src: trimmed };
  }
  return { kind: "video", src: abs };
}

function uniqueTrimmedUrls(list) {
  const seen = new Set();
  const out = [];
  for (const raw of list) {
    const s = raw == null ? "" : String(raw).trim();
    if (!s || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

function TheaterPhotoGallery({ theaterName, imageUrls, promoVideoUrls }) {
  const urls = useMemo(
    () => (Array.isArray(promoVideoUrls) ? promoVideoUrls : []),
    [promoVideoUrls]
  );
  const { theatreGalleryByKey } = useContext(Context);
  const images = useMemo(() => {
    if (Array.isArray(imageUrls) && imageUrls.length) return imageUrls;
    return resolveTheaterGallery(theaterName, null, theatreGalleryByKey);
  }, [imageUrls, theaterName, theatreGalleryByKey]);
  const [activeIx, setActiveIx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [modalSlideIx, setModalSlideIx] = useState(0);
  const hasVideo = urls.length > 0;

  const slides = useMemo(() => {
    const out = [];
    for (const src of images) {
      out.push({ kind: "image", src });
    }
    for (const url of urls) {
      out.push({ kind: "video", url });
    }
    return out;
  }, [images, urls]);

  useEffect(() => {
    setModalSlideIx((i) =>
      slides.length ? Math.min(i, slides.length - 1) : 0
    );
  }, [slides.length]);

  useEffect(() => {
    setActiveIx(0);
  }, [theaterName]);

  useEffect(() => {
    if (images.length <= 1 || paused) return undefined;
    const ms = 4500;
    const id = window.setInterval(() => {
      setActiveIx((prev) => (prev + 1) % images.length);
    }, ms);
    return () => window.clearInterval(id);
  }, [images.length, paused, theaterName]);

  const safeIx =
    images.length > 0 ? Math.min(activeIx, images.length - 1) : 0;

  const closeMediaModal = () => {
    setShowMediaModal(false);
  };

  const openPhotoLightbox = () => {
    if (!images.length) return;
    setModalSlideIx(Math.min(activeIx, images.length - 1));
    setShowMediaModal(true);
  };

  const openVideoModal = (e) => {
    e.stopPropagation();
    const firstVideoIx = images.length > 0 ? images.length : 0;
    setModalSlideIx(firstVideoIx);
    setShowMediaModal(true);
  };

  const safeModalIx =
    slides.length > 0 ? Math.min(modalSlideIx, slides.length - 1) : 0;
  const slide = slides[safeModalIx];
  const slideVideoEmbed =
    slide?.kind === "video"
      ? resolvePromoVideoEmbed(slide.url)
      : { kind: "video", src: "" };

  const videoControl = hasVideo ? (
    <button
      type="button"
      className="absolute right-2 top-2 z-[3] grid h-10 w-10 place-items-center rounded-full bg-black/55 text-white shadow-lg ring-1 ring-white/30 backdrop-blur-sm transition hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-400"
      aria-label={`Play ${theaterName} promo video`}
      onClick={openVideoModal}
    >
      <i className="bi bi-play-fill text-2xl leading-none" aria-hidden />
    </button>
  ) : null;

  const mediaModal = (
    <Modal
      show={showMediaModal}
      onHide={closeMediaModal}
      centered
      className="party-boss-promo-modal-root"
      contentClassName="party-boss-promo-modal-content party-boss-theatre-video-modal-content border-0 overflow-hidden"
      dialogClassName="party-boss-promo-modal-dialog party-boss-theatre-video-modal-dialog"
      backdropClassName="party-boss-promo-modal-backdrop"
    >
      <div className="party-boss-promo-modal-glow" aria-hidden />
      <button
        type="button"
        className="btn-close btn-close-white party-boss-promo-modal-close"
        aria-label="Close"
        onClick={closeMediaModal}
      />
      <Modal.Body className="p-0 overflow-hidden">
        {slides.length > 0 && slide ? (
          <div className="relative min-h-[200px] bg-black party-boss-theatre-video-modal-stage">
            {slides.length > 1 ? (
              <>
                <button
                  type="button"
                  className="party-boss-theatre-video-carousel-btn party-boss-theatre-video-carousel-btn--prev"
                  aria-label="Previous"
                  onClick={() =>
                    setModalSlideIx(
                      (i) => (i - 1 + slides.length) % slides.length
                    )
                  }
                >
                  <i className="bi bi-chevron-left fs-4" aria-hidden />
                </button>
                <button
                  type="button"
                  className="party-boss-theatre-video-carousel-btn party-boss-theatre-video-carousel-btn--next"
                  aria-label="Next"
                  onClick={() =>
                    setModalSlideIx((i) => (i + 1) % slides.length)
                  }
                >
                  <i className="bi bi-chevron-right fs-4" aria-hidden />
                </button>
              </>
            ) : null}
            {slide.kind === "image" ? (
              <div className="flex max-h-[min(70vh,720px)] items-center justify-center px-2 py-4 sm:px-4">
                <img
                  src={slide.src}
                  alt={`${theaterName} — ${safeModalIx + 1} of ${slides.length}`}
                  className="max-h-[min(70vh,720px)] w-full object-contain"
                />
              </div>
            ) : slideVideoEmbed.src ? (
              <div className="px-2 py-4 sm:px-4">
                <div className="ratio ratio-16x9 bg-black">
                  {slideVideoEmbed.kind === "iframe" ? (
                    <iframe
                      key={`${safeModalIx}-${slideVideoEmbed.src}`}
                      title={`${theaterName} promo`}
                      src={slideVideoEmbed.src}
                      className="border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : (
                    <video
                      key={`${safeModalIx}-${slideVideoEmbed.src}`}
                      controls
                      playsInline
                      className="position-absolute top-0 start-0 h-100 w-100"
                      style={{ objectFit: "contain" }}
                      src={slideVideoEmbed.src}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div className="p-4 text-center text-white small">
                Video URL missing.
              </div>
            )}
            {slides.length > 1 ? (
              <div
                className="party-boss-theatre-video-counter"
                aria-live="polite"
              >
                {safeModalIx + 1} / {slides.length}
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal.Body>
    </Modal>
  );

  if (!images.length) {
    return (
      <>
        {mediaModal}
        <div
          className="relative h-[180px] overflow-hidden rounded-2xl ring-1 ring-slate-200/70 sm:h-[200px]"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={() => setPaused(false)}
        >
          {videoControl}
          <div className="relative flex h-full items-center justify-center bg-slate-200 text-sm font-semibold text-slate-500 ring-1 ring-slate-200/80">
            No image
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {mediaModal}
      <div
        className="relative h-[180px] overflow-hidden rounded-2xl ring-1 ring-slate-200/70 sm:h-[200px]"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={() => setPaused(false)}
      >
        {videoControl}
        <div
          role="button"
          tabIndex={0}
          aria-label={`Open ${theaterName} photos${hasVideo ? " and video" : ""} in a larger view`}
          className="relative h-full w-full cursor-pointer bg-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-400 focus-visible:ring-offset-2"
          onClick={openPhotoLightbox}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openPhotoLightbox();
            }
          }}
        >
          {images.map((src, i) => (
            <img
              key={`${theaterName}-slide-${i}`}
              src={src}
              alt={i === safeIx ? `${theaterName} — ${i + 1}` : ""}
              aria-hidden={i !== safeIx}
              className={`pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out ${
                i === safeIx ? "z-[1] opacity-100" : "z-0 opacity-0"
              }`}
            />
          ))}
        </div>
        {images.length > 1 && (
          <div
            className="absolute bottom-2 left-0 right-0 z-[2] flex justify-center gap-1.5 px-2"
            role="tablist"
            aria-label={`${theaterName} photo carousel`}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {images.map((_, i) => (
              <button
                key={`${theaterName}-dot-${i}`}
                type="button"
                role="tab"
                aria-selected={i === safeIx}
                aria-label={`Show photo ${i + 1} of ${images.length}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIx(i);
                }}
                className={`h-2 min-w-[8px] rounded-full transition ${
                  i === safeIx
                    ? "w-6 bg-white shadow-sm"
                    : "w-2 bg-white/45 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function slotsAvailabilityBadgeClass(count) {
  const n = Number(count);
  if (Number.isNaN(n) || n < 0) {
    return "border-slate-300 bg-slate-100 text-slate-600";
  }
  if (n === 0) {
    return "border-red-600/80 bg-red-50 text-red-900";
  }
  if (n === 1) {
    return "border-red-500 bg-red-50 text-red-900";
  }
  if (n >= 5) {
    return "border-emerald-600/80 bg-emerald-50 text-emerald-900";
  }
  return "border-amber-500/90 bg-amber-50 text-amber-950";
}

function defaultExperienceRows(extraPer) {
  const extra =
    extraPer != null && extraPer !== "" && !Number.isNaN(Number(extraPer))
      ? Number(extraPer)
      : 349;

  return [
    {
      key: "extra",
      icon: "bi-currency-rupee",
      text: `+ ₹${extra}/extra person (50% discount for kids below 10yrs)`,
    },
    {
      key: "couple",
      icon: "bi-heart-fill",
      text: "Couple Friendly",
    },
    {
      key: "screen",
      icon: "bi-tv",
      text: "150 inch 4k screen",
    },
    {
      key: "audio",
      icon: "bi-volume-up-fill",
      text: "1000W Dolby atmos",
    },
    {
      key: "addons",
      icon: "bi-camera-fill",
      text: "Add Cakes and Photography in the next step",
    },
    {
      key: "refund",
      icon: "bi-shield-check",
      text: "Refund eligible if cancelled 72 hours before the slot time",
      iconTone: "blue",
    },
  ];
}

/** Feature lines — from admin theatre-gallery JSON when present, else legacy defaults. */
function TheaterExperienceHighlights({ lines, extraPer }) {
  const fallback = useMemo(() => defaultExperienceRows(extraPer), [extraPer]);
  const rows = useMemo(() => {
    if (!Array.isArray(lines) || lines.length === 0) return fallback;
    const mapped = lines
      .map((l, i) => {
        const text = String(l?.text ?? "").trim();
        if (!text) return null;
        const rawIcon = String(l?.icon ?? "").trim();
        const icon = rawIcon.startsWith("bi-") ? rawIcon : rawIcon ? `bi-${rawIcon}` : "bi-star";
        return { key: `dyn-${i}`, icon, text, iconTone: "green" };
      })
      .filter(Boolean);
    return mapped.length ? mapped : fallback;
  }, [lines, fallback]);

  return (
    <ul className="mb-3 list-none space-y-2.5 p-0">
      {rows.map((row) => {
        const green = row.iconTone !== "blue";
        return (
          <li key={row.key} className="flex gap-2.5">
            <span
              className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-base leading-none ${
                green
                  ? "bg-[#e8f5ee] text-[#1a7f4b]"
                  : "bg-[#eef6ff] text-[#1b6ab0]"
              }`}
              aria-hidden
            >
              <i className={`bi ${row.icon}`} />
            </span>
            <span className="text-[13px] font-medium leading-snug text-slate-700 sm:text-sm">
              {row.text}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function TheatersList({ TheatersList: theaters, getTheaterDetails }) {
  const { cms } = useCms();
  const {
    context,
    theatreGalleryByKey,
    theatreVideoByKey,
    theatreExperienceByKey,
    addonMetaData,
  } = useContext(Context);
  const eventDay = useMemo(
    () => toDateInputValue(context?.eventDate),
    [context?.eventDate]
  );

  const [activeSlotIndex, setActiveSlotIndex] = useState({});
  const defaultLoc = "Hyderabad";

  /** Only an explicitly clicked slot counts — no auto-pick of first available. */
  const slotIndexFor = useCallback(
    (list) => {
      const tid = list.id ?? list.name;
      const manual = activeSlotIndex[tid];
      if (manual === undefined) return -1;
      const slot = list?.timeslots?.[manual];
      if (slot && !isSlotDisabled(slot, eventDay)) return manual;
      return -1;
    },
    [activeSlotIndex, eventDay]
  );

  const setSlotForTheater = useCallback((theaterId, index) => {
    setActiveSlotIndex((prev) => ({ ...prev, [theaterId]: index }));
  }, []);

  return (
    <div
      className="mx-auto grid w-full gap-4 sm:gap-5 lg:gap-6 [grid-template-columns:repeat(auto-fit,minmax(min(100%,280px),400px))] [justify-content:center]"
    >
      {theaters?.map((list) => {
        const tid = list.id ?? list.name;
        const idx = slotIndexFor(list);
        const slot = idx >= 0 ? list?.timeslots?.[idx] : undefined;
        const previewSlot =
          slot && !isSlotDisabled(slot, eventDay)
            ? slot
            : list.timeslots?.find((s) => !isSlotDisabled(s, eventDay));
        const priceSlot =
          idx >= 0 && slot && !isSlotDisabled(slot, eventDay)
            ? slot
            : previewSlot;
        const slotOffers = addonMetaData?.slotOffers ?? [];
        const mergedPriceSlot =
          priceSlot && !isSlotDisabled(priceSlot, eventDay)
            ? mergeSlotWithOffer(priceSlot, list.id, eventDay, slotOffers)
            : null;
        const slotOfferBottom =
          idx >= 0 &&
          slot &&
          !isSlotDisabled(slot, eventDay) &&
          slot?.id != null
            ? findApplicableSlotOffer(slotOffers, list.id, slot.id, eventDay)
            : null;
        const basePrice =
          mergedPriceSlot?.basePrice ?? priceSlot?.basePrice ?? list.basePrice;
        const extraPer =
          priceSlot?.extraPerPerson ?? list.extraPersonPrice;
        const priceLabel =
          basePrice != null && !Number.isNaN(basePrice) ? `₹${basePrice}` : "—";
        const capacityPeople =
          priceSlot?.defaultCapacity ?? list.defaultCapacity ?? "—";
        const slotsTone = slotsAvailabilityBadgeClass(list.availableSlots);
        const slotsLabel = `${list.availableSlots ?? 0} Slots Available`;
        const mediaEntry = getTheaterMediaEntry(list.name, cms.theaterMedia);
        const galleryUrls = resolveTheaterGallery(
          list.name,
          cms.theaterMedia,
          theatreGalleryByKey
        );
        const tKey = normalizeTheaterFolderKey(list.name);
        const experienceLines = theatreExperienceByKey?.[tKey]?.lines;
        const uploadedRaw = theatreVideoByKey?.[tKey];
        const uploadedList = Array.isArray(uploadedRaw)
          ? uploadedRaw
          : uploadedRaw
            ? [uploadedRaw]
            : [];
        const cmsExtras = [mediaEntry?.theatreVideoUrl, mediaEntry?.branchVideoUrl]
          .map((s) => (s ? String(s).trim() : ""))
          .filter(Boolean);
        const promoVideoUrls = uniqueTrimmedUrls([...uploadedList, ...cmsExtras]);

        const selectableCount =
          list.timeslots?.filter((s) => !isSlotDisabled(s, eventDay)).length ??
          0;
        const canContinue =
          selectableCount > 0 &&
          idx >= 0 &&
          slot &&
          !isSlotDisabled(slot, eventDay) &&
          list.availableSlots > 0;

        return (
          <div
            key={list.id ?? list.name}
            className="party-boss-booking-card party-boss-reveal active overflow-hidden rounded-[26px] border border-slate-200 bg-white text-[#06132b] shadow-[0_12px_40px_-12px_rgba(15,23,42,0.15)] ring-1 ring-slate-900/[0.06] transition duration-500"
          >
            <div className="px-2.5 pb-2.5 pt-2.5 sm:px-3 sm:pb-3 sm:pt-3">
            <TheaterPhotoGallery
              theaterName={list.name}
              imageUrls={galleryUrls}
              promoVideoUrls={promoVideoUrls}
            />
            </div>
            <div className="p-4 pt-3 sm:p-5 sm:pt-3">
              <div className="mb-2 flex justify-between gap-2">
                <h3 className="text-xl font-black leading-tight sm:text-2xl">
                  {list.name} Theatre
                </h3>
                <div className="flex shrink-0 gap-1.5">
                  <div className="grid h-10 w-9 place-items-center rounded-lg bg-slate-200 text-center text-[9px] font-black leading-tight">
                    📍
                    <br />
                    MAPS
                  </div>
                  <div className="grid h-10 w-9 place-items-center rounded-lg bg-slate-200 text-center text-[9px] font-black leading-tight">
                    🍴
                    <br />
                    FOOD
                  </div>
                </div>
              </div>
              <div className="mb-2 flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-700">
                <span>📍 {list.location || defaultLoc}</span>
                <span
                  className={`rounded-full border px-2.5 py-1 text-xs font-bold ${slotsTone}`}
                >
                  {slotsLabel}
                </span>
              </div>
              <div className="mb-3 flex flex-wrap gap-x-3 gap-y-1 text-[13px] text-[#101a34]">
                <span>👥 Max {list.maxGuests ?? "—"} People</span>
                <span>☕ Food*</span>
                <span>🎊 Decor</span>
              </div>
              <h4 className="mb-1.5 text-base font-black sm:text-lg">
                Choose Your Slot -
              </h4>
              <div className="mb-3 flex flex-wrap gap-1.5">
                {list.timeslots?.map((ts, tidx) => {
                  const apiBooked =
                    ts.isAvailable === false || ts.isAvailable === "N";
                  const disabled = isSlotDisabled(ts, eventDay);
                  const timeEnded = disabled && !apiBooked;
                  const strikeSlot = apiBooked || timeEnded;
                  const isSelected = idx >= 0 && idx === tidx && !disabled;
                  const offerThis =
                    !disabled && ts?.id != null
                      ? findApplicableSlotOffer(slotOffers, list.id, ts.id, eventDay)
                      : null;
                  const offerHeadline =
                    offerThis?.headline && String(offerThis.headline).trim()
                      ? String(offerThis.headline).trim()
                      : "";

                  return (
                    <button
                      key={ts.id ?? tidx}
                      type="button"
                      disabled={disabled}
                      aria-pressed={isSelected}
                      onClick={() => {
                        if (disabled) return;
                        setSlotForTheater(tid, tidx);
                      }}
                      title={
                        apiBooked
                          ? "Already booked"
                          : timeEnded
                            ? "This slot time has passed"
                            : ""
                      }
                      className={`max-w-[140px] rounded-xl border px-2.5 py-1.5 text-left text-[11px] leading-snug transition-all duration-200 sm:max-w-none sm:px-3 sm:text-xs ${
                        isSelected
                          ? "scale-[1.02] border-pink-500 bg-gradient-to-br from-pink-50 via-fuchsia-50 to-violet-100 font-bold text-[#5b21b6] shadow-[0_6px_16px_-6px_rgba(236,72,153,0.55)] ring-2 ring-pink-400/50 ring-offset-2 ring-offset-white"
                          : "border-slate-200 bg-slate-50 text-slate-600"
                      } ${
                        disabled
                          ? `cursor-not-allowed opacity-55${
                              strikeSlot ? " line-through" : ""
                            }`
                          : isSelected
                            ? "cursor-pointer"
                            : "cursor-pointer hover:border-pink-300 hover:bg-white hover:shadow-sm"
                      }`}
                    >
                      {String(ts.slotTime || "")
                        .replace(" - ", " -\n")
                        .split("\n")
                        .map((line, li) => (
                          <React.Fragment key={`${tidx}-${li}`}>
                            {line}
                            <br />
                          </React.Fragment>
                        ))}
                      {offerHeadline ? (
                        <span className="mt-0.5 block text-[9px] font-semibold leading-tight text-red-600">
                          {offerHeadline}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
              <TheaterExperienceHighlights lines={experienceLines} extraPer={extraPer} />
              <div className="mb-1 flex flex-wrap items-end gap-2">
                <div className="text-2xl font-black sm:text-3xl">
                  {priceLabel}
                </div>
                <span className="text-sm text-slate-500">
                  for up to {capacityPeople} people
                </span>
              </div>
              {slotOfferBottom?.headline && String(slotOfferBottom.headline).trim() ? (
                <p className="mb-2 text-xs font-semibold leading-snug text-red-600">
                  {String(slotOfferBottom.headline).trim()}
                </p>
              ) : null}
              <button
                type="button"
                disabled={!canContinue}
                onClick={() => {
                  if (!canContinue || idx < 0) return;
                  getTheaterDetails(list, idx);
                }}
                className="mt-1 w-full rounded-2xl bg-gradient-to-r from-pink-500 to-violet-600 py-2.5 text-center text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-50 sm:py-3 sm:text-base"
              >
                {!canContinue && selectableCount === 0 ? (
                  "No slots left today"
                ) : (
                  "Proceed"
                )}
              </button>
              {selectableCount > 0 && (
                <p className="mt-2 text-center text-[11px] font-bold leading-snug text-slate-600 sm:text-xs">
                  (Just Pay ₹700 Now)
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default TheatersList;
